import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useKai, KaiReaction } from '../contexts/KaiPresenceContext';
import { Shield, Moon } from 'lucide-react';

interface Props {
    whisper?: string;
    inline?: boolean;
    size?: number;
    className?: string;
}

export const KaiAvatar: React.FC<Props> = ({ whisper, inline = false, size = 72, className = "" }) => {
  const { state, setMood } = useKai();
  const [position, setPosition] = useState<{ x: number, y: number }>({ x: 0, y: 0 });
  
  // Use context size if available, otherwise prop size
  const currentSize = state.customSize || size;

  // --- Biological State ---
  const [lookAt, setLookAt] = useState({ x: 0, y: 0 }); // -1 to 1 range
  const [lightSource, setLightSource] = useState({ x: 50, y: 50 }); // % range
  const [isBlinking, setIsBlinking] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  // --- Refs ---
  const avatarRef = useRef<HTMLDivElement>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blinkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animationFrameRef = useRef<number>(0);

  // --- Configuration ---
  const IDLE_TIMEOUT = 30000;

  // ----------------------------------------------------------------------
  // 1. BIOLOGICAL ENGINE (Blinking & Breathing)
  // ----------------------------------------------------------------------
  
  useEffect(() => {
      // Blinking Logic
      const triggerBlink = () => {
          if (state.mood === 'sleeping') return;
          setIsBlinking(true);
          setTimeout(() => setIsBlinking(false), 150);
          
          // Random next blink between 2s and 6s
          const nextBlink = Math.random() * 4000 + 2000;
          blinkTimerRef.current = setTimeout(triggerBlink, nextBlink);
      };
      
      triggerBlink();
      return () => {
          cancelAnimationFrame(animationFrameRef.current);
          if (blinkTimerRef.current) clearTimeout(blinkTimerRef.current);
      };
  }, [state.mood]);

  // ----------------------------------------------------------------------
  // 2. SENSORY ENGINE (Mouse Tracking & Subsurface Scattering)
  // ----------------------------------------------------------------------

  const wakeUp = useCallback(() => {
      if (state.mood === 'sleeping') {
          setMood('idle');
      }
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
          if (state.mood === 'idle' && !inline) { 
              setMood('sleeping');
          }
      }, IDLE_TIMEOUT);
  }, [state.mood, setMood, inline]);

  useEffect(() => {
      const handleActivity = (e: MouseEvent | KeyboardEvent | Event) => {
          wakeUp();
          
          if (e.type === 'mousemove' && avatarRef.current && state.mood !== 'sleeping') {
              const rect = avatarRef.current.getBoundingClientRect();
              const centerX = rect.left + rect.width / 2;
              const centerY = rect.top + rect.height / 2;
              const mouseE = e as MouseEvent;

              // Vector calculations
              const dx = mouseE.clientX - centerX;
              const dy = mouseE.clientY - centerY;
              
              // 1. Eye Tracking (Normalized -1 to 1)
              // Clamped to avoid looking "too far" back
              const maxDist = 500;
              const normX = Math.max(-1, Math.min(1, dx / maxDist));
              const normY = Math.max(-1, Math.min(1, dy / maxDist));
              setLookAt({ x: normX, y: normY });

              // 2. Lighting Calculation (Subsurface Scattering)
              // Invert the light source slightly to mimic light passing THROUGH liquid
              // If mouse is Top-Right, inner glow moves Bottom-Left
              const lightX = 50 - (normX * 40); 
              const lightY = 50 - (normY * 40);
              setLightSource({ x: lightX, y: lightY });
          }
      };

      window.addEventListener('mousemove', handleActivity);
      window.addEventListener('keydown', handleActivity);
      window.addEventListener('click', handleActivity);
      wakeUp();

      return () => {
          window.removeEventListener('mousemove', handleActivity);
          window.removeEventListener('keydown', handleActivity);
          window.removeEventListener('click', handleActivity);
          if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      };
  }, [wakeUp, state.mood]);

  // ----------------------------------------------------------------------
  // 3. LOCOMOTION ENGINE (Flight & Positioning)
  // ----------------------------------------------------------------------

  useEffect(() => {
    if (inline) return;

    const updatePosition = () => {
      let targetPos = { x: 0, y: 0 };
      const isMobile = window.innerWidth < 768;

      if (state.targetId) {
        const el = document.getElementById(state.targetId);
        if (el) {
          const rect = el.getBoundingClientRect();
          
          if (state.placement === 'center') {
              targetPos = {
                  x: rect.left + rect.width / 2 - (currentSize / 2),
                  y: rect.top + rect.height / 2 - (currentSize / 2)
              };
          } else {
              // Hover above
              targetPos = { 
                x: rect.left + rect.width / 2 - (currentSize / 2), 
                y: rect.top - currentSize + 10 
              };
          }
        } else {
            targetPos = { x: window.innerWidth - (isMobile ? 80 : 120), y: window.innerHeight - (isMobile ? 140 : 120) };
        }
      } else {
        // Home Position
        targetPos = { x: window.innerWidth - (isMobile ? 80 : 120), y: window.innerHeight - (isMobile ? 140 : 120) };
      }
      setPosition(targetPos);
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [state.targetId, state.placement, inline, currentSize]);

  if (!state.isEnabled && !inline) return null;

  // ----------------------------------------------------------------------
  // 4. RENDER CONFIG (Liquid Soul Aesthetic)
  // ----------------------------------------------------------------------

  const getMoodConfig = () => {
      // Base Color Logic
      const core = state.color || 'indigo';
      let colors = {
          base: 'from-indigo-500 via-purple-500 to-pink-500',
          glow: 'rgba(99, 102, 241, 0.5)'
      };

      if (core === 'rose') colors = { base: 'from-rose-400 via-pink-500 to-fuchsia-600', glow: 'rgba(244,63,94,0.5)' };
      if (core === 'emerald') colors = { base: 'from-emerald-400 via-teal-500 to-cyan-600', glow: 'rgba(16,185,129,0.5)' };
      if (core === 'amber') colors = { base: 'from-amber-400 via-orange-500 to-red-500', glow: 'rgba(245,158,11,0.5)' };

      // Mood Overrides
      if (state.mood === 'protective') colors = { base: 'from-red-600 via-red-500 to-rose-900', glow: 'rgba(220, 38, 38, 0.8)' };
      if (state.mood === 'excited') colors = { base: 'from-amber-300 via-orange-500 to-yellow-500', glow: 'rgba(245, 158, 11, 0.8)' };
      if (state.mood === 'sleeping') colors = { base: 'from-slate-400 via-slate-500 to-slate-600', glow: 'rgba(148, 163, 184, 0.3)' };
      
      // Listening Mood: slightly brighter/alert blue
      if (state.mood === 'listening') colors = { base: 'from-blue-500 via-indigo-500 to-violet-500', glow: 'rgba(99, 102, 241, 0.6)' };

      // Shape Morphing (Physiology)
      let morphClass = 'animate-morph-slow';
      
      if (state.mood === 'thinking') morphClass = 'animate-morph-fast';
      if (state.mood === 'talking') morphClass = 'animate-morph-vertical';
      if (state.mood === 'excited') morphClass = 'animate-morph-wild';
      if (state.mood === 'protective') morphClass = 'rounded-[40%] scale-110 duration-200'; // Rigid
      
      // Listening: "Leans forward 5°" - We use rotation and a slight scale up to simulate leaning towards the user
      if (state.mood === 'listening') morphClass = 'animate-morph-slow rotate-6 scale-105 duration-500 ease-out';

      // Sleeping: Shrinks significantly and flattens
      if (state.mood === 'sleeping') morphClass = 'rounded-[50%_50%_40%_40%_/_50%_50%_30%_30%] scale-75 opacity-70'; 

      // Eye Geometry (Expressive)
      let eyeShape = { h: 0.15, w: 0.1, r: '50%' }; // Standard Oval
      if (state.mood === 'excited') eyeShape = { h: 0.22, w: 0.22, r: '50%' }; // Big Circles
      if (state.mood === 'thinking') eyeShape = { h: 0.08, w: 0.12, r: '50%' }; // Squint
      if (state.mood === 'protective') eyeShape = { h: 0.08, w: 0.25, r: '0%' }; // Sharp/Narrow
      if (state.mood === 'talking') eyeShape = { h: 0.18, w: 0.12, r: '50%' }; // Tall/Alert
      if (state.mood === 'listening') eyeShape = { h: 0.18, w: 0.12, r: '50%' }; // Wide/Attentive

      return { colors, morphClass, eyeShape };
  };

  const config = getMoodConfig();

  // Eye Calculation
  const eyeW = currentSize * config.eyeShape.w;
  const eyeH = isBlinking || state.mood === 'sleeping' ? 2 : currentSize * config.eyeShape.h;
  const eyeRadius = config.eyeShape.r;
  
  // Eye Offset (Parallax)
  const eyeOffsetX = lookAt.x * (currentSize * 0.12);
  const eyeOffsetY = lookAt.y * (currentSize * 0.12);

  // Transient Reaction Class (Physics)
  let reactionClass = '';
  if (state.reaction === 'nod') reactionClass = 'animate-nod';
  else if (state.reaction === 'bounce') reactionClass = 'animate-bounce-reaction';
  else if (state.reaction === 'shake') reactionClass = 'animate-shake-reaction';
  // Pulse: An energy bloom (brightness + scale)
  else if (state.reaction === 'pulse') reactionClass = 'animate-pulse-reaction';

  // Smoothness Logic:
  // 1. Blinking needs to be fast (75ms).
  // 2. Mood Morphing needs to be slow (700ms).
  // 3. We determine the transition duration dynamically.
  const eyeTransitionDuration = isBlinking ? 'duration-75' : 'duration-700';

  // ----------------------------------------------------------------------
  // 5. REACTION MAPPING (Emote Bubbles)
  // ----------------------------------------------------------------------
  const getReactionEmote = (r: KaiReaction) => {
      switch (r) {
          case 'nod': return { icon: '✨', label: 'Noted', color: 'emerald' };
          case 'shake': return { icon: '🤔', label: 'Hmm...', color: 'amber' };
          case 'bounce': return { icon: '🔥', label: 'Resonance!', color: 'rose' };
          case 'pulse': return { icon: '⚡', label: 'Syncing', color: 'indigo' };
          default: return null;
      }
  };
  const activeEmote = getReactionEmote(state.reaction);

  // --- STYLE HELPERS ---
  const getBubbleStyle = (color: string) => {
      switch (color) {
          case 'rose': return 'border-rose-200 dark:border-rose-800 text-rose-900 dark:text-rose-100 bg-white/95 dark:bg-rose-950/90 shadow-rose-500/20';
          case 'emerald': return 'border-emerald-200 dark:border-emerald-800 text-emerald-900 dark:text-emerald-100 bg-white/95 dark:bg-emerald-950/90 shadow-emerald-500/20';
          case 'amber': return 'border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100 bg-white/95 dark:bg-amber-950/90 shadow-amber-500/20';
          default: return 'border-indigo-200 dark:border-indigo-800 text-indigo-900 dark:text-indigo-100 bg-white/95 dark:bg-indigo-950/90 shadow-indigo-500/20';
      }
  };
  
  const currentBubbleStyle = getBubbleStyle(state.color || 'indigo');

  return (
    <>
        {/* SVG Filter Definition (Once globally) */}
        <svg className="absolute w-0 h-0 pointer-events-none">
            <defs>
                <filter id="kai-liquid">
                    <feTurbulence type="fractalNoise" baseFrequency="0.01" numOctaves="3" result="warp" />
                    <feDisplacementMap xChannelSelector="R" yChannelSelector="G" scale="20" in="SourceGraphic" in2="warp" />
                </filter>
            </defs>
        </svg>

        <style dangerouslySetInnerHTML={{__html: `
            @keyframes morph-slow {
                0% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
                50% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
                100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
            }
            @keyframes morph-fast {
                0% { border-radius: 40% 60% 60% 40% / 60% 30% 70% 40%; transform: rotate(0deg); }
                100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; transform: rotate(360deg); }
            }
            @keyframes morph-vertical {
                0%, 100% { border-radius: 50%; transform: scaleY(1); }
                50% { border-radius: 45% 45% 55% 55% / 55% 55% 45% 45%; transform: scaleY(0.95) translateY(5px); }
            }
            @keyframes morph-wild {
                0% { border-radius: 70% 30% 30% 70% / 60% 40% 60% 40%; transform: scale(1.1); }
                50% { border-radius: 30% 70% 70% 30% / 40% 60% 40% 60%; transform: scale(1.2) rotate(10deg); }
                100% { border-radius: 70% 30% 30% 70% / 60% 40% 60% 40%; transform: scale(1.1); }
            }
            
            /* REACTION ANIMATIONS (Physical Transform Only) */
            @keyframes nod {
                0%, 100% { transform: translateY(0); }
                30% { transform: translateY(12px) scaleY(0.95); }
                60% { transform: translateY(-4px) scaleY(1.02); }
            }
            @keyframes bounce-reaction {
                0%, 100% { transform: scale(1); }
                40% { transform: scale(1.25); }
                60% { transform: scale(0.95); }
            }
            @keyframes shake-reaction {
                0%, 100% { transform: translateX(0); }
                20% { transform: translateX(-5px); }
                40% { transform: translateX(5px); }
                60% { transform: translateX(-5px); }
                80% { transform: translateX(5px); }
            }
            @keyframes pulse-reaction {
                0% { transform: scale(1); filter: brightness(1); }
                50% { transform: scale(1.1); filter: brightness(1.3); }
                100% { transform: scale(1); filter: brightness(1); }
            }
            @keyframes pop-in {
                0% { transform: translateY(10px) scale(0.5); opacity: 0; }
                60% { transform: translateY(-5px) scale(1.1); opacity: 1; }
                100% { transform: translateY(0) scale(1); opacity: 1; }
            }
            @keyframes fade-out-up {
                0% { transform: translateY(0) scale(1); opacity: 1; }
                100% { transform: translateY(-20px) scale(0.9); opacity: 0; }
            }

            .animate-morph-slow { animation: morph-slow 8s ease-in-out infinite; }
            .animate-morph-fast { animation: morph-fast 2s linear infinite; }
            .animate-morph-vertical { animation: morph-vertical 1s ease-in-out infinite; }
            .animate-morph-wild { animation: morph-wild 0.5s linear infinite; }
            
            /* Applied to Wrapper Layer */
            .animate-nod { animation: nod 0.5s ease-out; }
            .animate-bounce-reaction { animation: bounce-reaction 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
            .animate-shake-reaction { animation: shake-reaction 0.4s ease-in-out; }
            .animate-pulse-reaction { animation: pulse-reaction 0.6s ease-in-out; }
            
            .animate-pop-in { animation: pop-in 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
        `}} />

        <div 
            className={`${inline ? 'relative inline-block' : 'fixed z-[100] pointer-events-none transition-all duration-1000 ease-in-out will-change-transform'} ${className}`}
            style={inline ? { width: currentSize, height: currentSize } : { 
                width: currentSize, height: currentSize,
                left: 0, 
                top: 0, 
                transform: `translate(${position.x}px, ${position.y}px)` 
            }}
        >
            <div 
                ref={avatarRef}
                className="relative group pointer-events-auto cursor-help"
                style={{ width: '100%', height: '100%' }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {/* --- 0. REACTION WRAPPER (Composition Layer) --- */}
                <div className={`w-full h-full transition-transform duration-300 ${reactionClass}`}>

                    {/* --- 1. HALO PARTICLES (Atmosphere) --- */}
                    <div className="absolute inset-[-20%] animate-spin-slow opacity-30 pointer-events-none">
                        <div className="absolute top-0 left-1/2 w-1 h-1 bg-white rounded-full shadow-[0_0_10px_white]"></div>
                        <div className="absolute bottom-1/4 right-0 w-1.5 h-1.5 bg-white/50 rounded-full"></div>
                    </div>

                    {/* --- 2. THE LIQUID SOUL (Main Body) --- */}
                    <div className={`absolute inset-0 transition-all duration-700 ease-out bg-gradient-to-br ${config.colors.base} ${config.morphClass}`}
                        style={{ 
                            boxShadow: `0 0 30px ${config.colors.glow}`,
                        }}
                    >
                        {/* Subsurface Scattering Gradient (Tracks Mouse) */}
                        <div 
                            className="absolute inset-0 rounded-[inherit] opacity-80 transition-opacity duration-300"
                            style={{
                                background: `radial-gradient(circle at ${lightSource.x}% ${lightSource.y}%, rgba(255,255,255,0.6) 0%, transparent 60%)`,
                                mixBlendMode: 'overlay'
                            }}
                        ></div>

                        {/* Specular Highlight (Rim Light) */}
                        <div className="absolute inset-[2px] rounded-[inherit] bg-gradient-to-t from-black/10 to-white/40 opacity-50"></div>
                    </div>

                    {/* --- 3. THE FACE (Floating on surface) --- */}
                    <div 
                        className={`absolute inset-0 flex flex-col items-center justify-center transition-transform duration-300 ease-out`}
                        style={{ 
                            transform: `translate(${eyeOffsetX}px, ${eyeOffsetY}px)` 
                        }}
                    >
                        {/* Eyes Container */}
                        <div className="flex gap-[18%] mb-[5%] items-center justify-center w-full">
                            {/* Left Eye */}
                            <div 
                                className={`bg-white shadow-[0_0_5px_rgba(255,255,255,0.8)] transition-all ${eyeTransitionDuration}`}
                                style={{ 
                                    width: eyeW, 
                                    height: eyeH, 
                                    borderRadius: state.mood === 'sleeping' ? '50%' : eyeRadius,
                                    transform: state.mood === 'protective' ? 'rotate(15deg)' : 'none'
                                }}
                            ></div>
                            {/* Right Eye */}
                            <div 
                                className={`bg-white shadow-[0_0_5px_rgba(255,255,255,0.8)] transition-all ${eyeTransitionDuration}`}
                                style={{ 
                                    width: eyeW, 
                                    height: eyeH, 
                                    borderRadius: state.mood === 'sleeping' ? '50%' : eyeRadius,
                                    transform: state.mood === 'protective' ? 'rotate(-15deg)' : 'none'
                                }}
                            ></div>
                        </div>

                        {/* Mouth (Only when talking/excited) */}
                        {(state.mood === 'talking' || state.mood === 'excited') && (
                            <div className="w-[20%] h-[10%] bg-white/80 rounded-full animate-pulse mt-1 shadow-sm"></div>
                        )}
                    </div>

                </div> {/* End Reaction Wrapper */}

                {/* --- 4. STATUS OVERLAYS (Outside Reaction Wrapper to avoid distortion) --- */}
                
                {state.mood === 'sleeping' && (
                    <div className="absolute -top-6 -right-6 animate-float-up">
                        <Moon size={currentSize * 0.25} className="text-slate-400 fill-slate-400/20" />
                        <div className="text-slate-400 font-bold text-xs absolute -top-2 left-6">z</div>
                    </div>
                )}

                {state.mood === 'thinking' && (
                    <div className="absolute inset-[-15%] border border-dashed border-white/30 rounded-full animate-spin-slow pointer-events-none"></div>
                )}

                {state.mood === 'protective' && (
                    <div className="absolute -bottom-2 -right-2 bg-red-600 text-white p-1.5 rounded-full shadow-lg animate-bounce z-10">
                        <Shield size={currentSize * 0.2} fill="currentColor" />
                    </div>
                )}

                {/* --- 5. CONTEXT AWARE BUBBLES --- */}
                
                {/* A. Transient Reaction Emote (Context Colored Pop-up) */}
                {activeEmote && (
                    <div className="absolute -top-14 left-1/2 -translate-x-1/2 animate-pop-in z-50 whitespace-nowrap pointer-events-none origin-bottom">
                        <div className={`
                            flex items-center gap-2 px-3 py-1.5 rounded-full shadow-xl border backdrop-blur-xl
                            ${currentBubbleStyle}
                        `}>
                            <span className="text-lg filter drop-shadow-sm">{activeEmote.icon}</span>
                            <span className="text-[10px] font-black uppercase tracking-widest">{activeEmote.label}</span>
                        </div>
                    </div>
                )}

                {/* B. Persistent Speech Bubble (The Whisper / Context) */}
                {(state.message || (isHovered && whisper && state.mood !== 'sleeping')) && (
                    <div className="absolute bottom-full right-0 mb-4 w-56 z-30 animate-pop-in origin-bottom-right">
                        <div className={`
                            relative backdrop-blur-xl 
                            p-4 rounded-[24px] rounded-br-sm shadow-2xl border 
                            transform transition-all duration-300 scale-100 opacity-100
                            ${currentBubbleStyle}
                        `}>
                            <div className="relative z-10">
                                {state.message ? (
                                    // Spoken Message
                                    <p className="text-sm font-semibold leading-relaxed">
                                        {state.message}
                                    </p>
                                ) : (
                                    // Whisper Hint
                                    <p className="text-xs font-medium italic opacity-90">
                                        "{whisper}"
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    </>
  );
};